//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as metricsApi from '@apis/metrics/metricsApi';
import {
  EcommerceSessionType,
  IStatsRequest,
} from '@apis/metrics/metricsApiModel';
import { initialBaseStatsRequest } from '@insights/insightsConstants';
import {
  IInsightsActivityStats,
  IInsightsBubbleStats,
  IInsightsContentAcquisitionStats,
  IInsightsExperienceStats,
  IInsightsGraphStats,
} from '@insights/redux/insightsPageModel';
import {
  fetchApiActivityStats,
  getTestVariationFromParams,
} from '@insights/redux/insightsPageUtils';
import { getMetadata } from '@insights/redux/metadata/insightsMetadataSlice';
import { IInsightsSummaryPageState } from '@insights/redux/summary/insightsSummaryModel';
import { ILabel } from '@models/ILabel';
import Interval from '@models/Interval';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import IntervalHelper from '@utils/intervalHelper';

const initialState: IInsightsSummaryPageState = {
  statsRequestBase: undefined,
  bubbleStats: undefined,
  experienceStats: undefined,
  contentAcquisitionStats: undefined,
  activityStats: initialBaseStatsRequest,
  allLabels: undefined,
  flags: {
    isFetchingBubbleStats: false,
    isFetchingExperienceStats: false,
    isFetchingContentAcquisitionStats: false,
    isFetchingActivityStats: false,
    isFetchingGraphStats: false,
    isFetchingLabels: false,
  },
  errors: {
    fetchBubbleStatsError: undefined,
    fetchExperienceStatsError: undefined,
    fetchContentAcquisitionStatsError: undefined,
    fetchActivityStatsError: undefined,
    fetchGraphStatsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InsightsSummary,
  initialState,
  reducers: {
    requestBubbleStats(state) {
      state.flags.isFetchingBubbleStats = true;
      state.errors.fetchBubbleStatsError = undefined;
    },
    recieveBubbleStatsSucceeded(
      state,
      action: PayloadAction<IInsightsBubbleStats>,
    ) {
      state.flags.isFetchingBubbleStats = false;
      state.bubbleStats = action.payload;
    },
    recieveBubbleStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingBubbleStats = false;
      state.errors.fetchBubbleStatsError = action.payload;
    },
    requestGraphStats(state) {
      state.flags.isFetchingGraphStats = true;
      state.errors.fetchGraphStatsError = undefined;
    },
    recieveGraphStatsSucceeded(
      state,
      action: PayloadAction<IInsightsGraphStats>,
    ) {
      state.flags.isFetchingGraphStats = false;
      state.graphStats = action.payload;
    },
    recieveGraphStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingGraphStats = false;
      state.errors.fetchGraphStatsError = action.payload;
    },
    requestActivityStats(state) {
      state.flags.isFetchingActivityStats = true;
      state.errors.fetchActivityStatsError = undefined;
    },
    recieveActivityStatsSucceeded(
      state,
      action: PayloadAction<IInsightsActivityStats>,
    ) {
      state.flags.isFetchingActivityStats = false;
      state.activityStats = action.payload;
    },
    recieveActivityStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingActivityStats = false;
      state.errors.fetchActivityStatsError = action.payload;
    },
    updateActivityStats(
      state,
      action: PayloadAction<Partial<IInsightsActivityStats>>,
    ) {
      state.activityStats = {
        ...state.activityStats,
        ...action.payload,
      };
    },
    requestExperienceStats(state) {
      state.flags.isFetchingExperienceStats = true;
      state.errors.fetchExperienceStatsError = undefined;
    },
    recieveExperienceStatsSucceeded(
      state,
      action: PayloadAction<IInsightsExperienceStats>,
    ) {
      state.flags.isFetchingExperienceStats = false;
      state.experienceStats = action.payload;
    },
    recieveExperienceStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingExperienceStats = false;
      state.errors.fetchExperienceStatsError = action.payload;
    },
    requestContentAcquisitionStats(state) {
      state.flags.isFetchingContentAcquisitionStats = true;
      state.errors.fetchContentAcquisitionStatsError = undefined;
    },
    recieveContentAcquisitionStatsSucceeded(
      state,
      action: PayloadAction<IInsightsContentAcquisitionStats>,
    ) {
      state.flags.isFetchingContentAcquisitionStats = false;
      state.contentAcquisitionStats = action.payload;
    },
    recieveContentAcquisitionStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingContentAcquisitionStats = false;
      state.errors.fetchContentAcquisitionStatsError = action.payload;
    },
    requestLabels(state) {
      state.flags.isFetchingLabels = true;
      state.errors.fetchLabelsError = undefined;
    },
    recieveLabelsSucceeded(state, action: PayloadAction<ILabel[]>) {
      state.flags.isFetchingLabels = false;
      state.allLabels = action.payload;
    },
    recieveLabelsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingLabels = false;
      state.errors.fetchLabelsError = action.payload;
    },
    setStatsRequestBase(state, action: PayloadAction<IStatsRequest>) {
      state.statsRequestBase = action.payload;
    },
    resetState(state) {
      state = initialState;
    },
  },
});

export const fetchBubbleStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.summary;
    const { dateRange } = getState().insights.metadata;
    if (statsRequestBase) {
      let statsRequest;
      if (statsRequestBase.comparisonInterval) {
        statsRequest = statsRequestBase;
      } else {
        const comparisonInterval = Interval.fromDateRange(
          IntervalHelper.getComparisonDateRange(dateRange),
        );
        statsRequest = {
          ...statsRequestBase,
          comparisonInterval,
        };
      }
      dispatch(actions.requestBubbleStats());
      try {
        const bubbleStatsRes = await metricsApi.getBubbleStats(statsRequest);
        const bubbleStats: IInsightsBubbleStats = {
          stats: bubbleStatsRes[0],
        };
        dispatch(actions.recieveBubbleStatsSucceeded(bubbleStats));
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveBubbleStatsFailed(err));
      }
    }
  },
);

export const fetchGraphStats = createThunk(() => async (dispatch, getState) => {
  const { statsRequestBase } = getState().insights.summary;
  if (statsRequestBase) {
    dispatch(actions.requestGraphStats());
    try {
      const graphStatsRes = await metricsApi.getGraphStats(statsRequestBase);
      const graphStats: IInsightsGraphStats = {
        stats: graphStatsRes,
      };
      dispatch(actions.recieveGraphStatsSucceeded(graphStats));
    } catch (e) {
      const err = getRequestErrorMessage(e) ?? '';
      dispatch(actions.recieveGraphStatsFailed(err));
    }
  }
});

export const fetchActivityStats = createThunk(
  () => async (dispatch, getState) => {
    const { activityStats, statsRequestBase } = getState().insights.summary;
    const { contentType, limit, offset, sortDir, sortKey } = activityStats;
    if (statsRequestBase) {
      dispatch(actions.requestActivityStats());
      try {
        const itemProductStatsRequest = {
          ...statsRequestBase,
          sortKey,
          sortDirection: sortDir,
          limit,
          offset,
          containerId: 'banner-image',
          excludeContainerId: true,
        };
        const activityStatsRes: IInsightsActivityStats =
          await fetchApiActivityStats(
            contentType,
            itemProductStatsRequest,
            activityStats,
          );
        dispatch(actions.recieveActivityStatsSucceeded(activityStatsRes));
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveActivityStatsFailed(err));
      }
    }
  },
);

export const fetchExperienceStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.summary;
    if (statsRequestBase) {
      dispatch(actions.requestExperienceStats());
      try {
        const touchpointStatsRes =
          await metricsApi.getTouchpointStats(statsRequestBase);
        dispatch(
          actions.recieveExperienceStatsSucceeded({
            stats: touchpointStatsRes,
          }),
        );
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveExperienceStatsFailed(err));
      }
    }
  },
);

export const fetchContentAcquisitionStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.summary;
    if (statsRequestBase) {
      dispatch(actions.requestContentAcquisitionStats());
      try {
        const contentAcquisitionStatsRes =
          await metricsApi.getContentAcquisitionStats(statsRequestBase);
        dispatch(
          actions.recieveContentAcquisitionStatsSucceeded({
            stats: contentAcquisitionStatsRes,
          }),
        );
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveContentAcquisitionStatsFailed(err));
      }
    }
  },
);

export const fetchLabels = createThunk(() => async (dispatch, getState) => {
  const { statsRequestBase } = getState().insights.summary;
  if (statsRequestBase) {
    dispatch(actions.requestLabels());
    try {
      const labelsResponse = await metricsApi.getLabels(statsRequestBase);
      dispatch(
        actions.recieveLabelsSucceeded(
          labelsResponse.map((label) => {
            return {
              label: label.label,
              count: label.count,
              value: label.label,
            };
          }),
        ),
      );
    } catch (e) {
      const err = getRequestErrorMessage(e) ?? '';
      dispatch(actions.recieveLabelsFailed(err));
    }
  }
});

export const initStatsRequestBase = createThunk(
  (revenueAttributionModel?: EcommerceSessionType) =>
    async (dispatch, getState) => {
      if (!getState().insights.metadata.flags.hasFetchedMetadata) {
        await dispatch(getMetadata());
      }
      const {
        createdOn,
        dateRange,
        // TODO: PD-263393: Streamline Revenue Attribution Model to React Query & Remove Redux Dependency
        revenueAttributionModel: metadataRevenueAttributionModel,
      } = getState().insights.metadata;
      dispatch(
        getStats({
          interval: IntervalHelper.fromDateRange(dateRange, createdOn),
          ecommerceSessionType:
            revenueAttributionModel ?? metadataRevenueAttributionModel,
          testVariation: getTestVariationFromParams(),
        }),
      );
    },
);

export const getStats = createThunk(
  (statsRequestBase: IStatsRequest) => (dispatch) => {
    dispatch(actions.setStatsRequestBase(statsRequestBase));
    void dispatch(fetchBubbleStats());
    void dispatch(fetchGraphStats());
    void dispatch(fetchExperienceStats());
    void dispatch(fetchActivityStats());
    void dispatch(fetchContentAcquisitionStats());
  },
);

export const { resetState, updateActivityStats } = actions;

export default reducer;
